'use client';
import React, { useCallback, useState, useEffect } from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputDescription from '@components/ui/DynamicForm/InputDescription';
import { cn } from '@components/lib/utils';

interface NumberRangeInputProps {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
  onHelpVisible: (visible: boolean) => void;
  disabled?: boolean;
}

const NumberRangeInput: React.FC<NumberRangeInputProps> = ({
  field,
  onChange,
  onHelpVisible,
  disabled = false,
}) => {
  const [_isHelpVisible, setIsHelpVisible] = useState(false);
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');

  const parseValue = useCallback(
    (value: string): { min: string; max: string } => {
      if (!value) return { min: '', max: '' };

      const isRangeFormat = /^\[.*\]$/.test(value.trim());
      if (!isRangeFormat) {
        return { min: '', max: '' };
      }

      try {
        const cleaned = value.replace(/^\[|\]$/g, '').replace(/"/g, '');
        const parts = cleaned
          .split(',')
          .map((p) => p.trim())
          .filter((p) => p);

        if (parts.length >= 2) {
          return {
            min: parts[0],
            max: parts[1],
          };
        }
        return { min: '', max: '' };
      } catch (_error) {
        return { min: '', max: '' };
      }
    },
    []
  );

  useEffect(() => {
    const parsed = parseValue(field.value as string);
    setMinValue(parsed.min);
    setMaxValue(parsed.max);

    if (field.value && !/^\[.*\]$/.test((field.value as string).trim())) {
      onChange(field, '');
    }
  }, [field.value, parseValue, onChange, field]);

  const handleMinChange = useCallback(
    (value: string) => {
      setMinValue(value);
      if (value && maxValue) {
        onChange(field, `[${value},${maxValue}]`);
      } else if (!value && !maxValue) {
        onChange(field, '');
      }
    },
    [field, onChange, maxValue]
  );

  const handleMaxChange = useCallback(
    (value: string) => {
      setMaxValue(value);
      if (minValue && value) {
        onChange(field, `[${minValue},${value}]`);
      } else if (!minValue && !value) {
        onChange(field, '');
      }
    },
    [field, onChange, minValue]
  );

  return (
    <div className="ml-auto relative overflow-visible flex items-center gap-1 justify-between w-full z-10 opacity-100">
      <div className="flex items-center flex-1 w-full min-w-0">
        <input
          type="number"
          placeholder="Min Value"
          value={minValue}
          onChange={(e) => handleMinChange(e.target.value)}
          disabled={disabled}
          className={cn(
            'pl-2 pr-2 py-0.5 border-l border-form-border',
            'bg-white text-left',
            'w-full min-w-0',
            !minValue ? 'text-xs text-neutral-400 py-1' : 'text-sm',
            'appearance-none',
            '[&::-webkit-inner-spin-button]:appearance-none',
            '[&::-webkit-outer-spin-button]:appearance-none',
            'focus-visible:outline-shade-aqua-300'
          )}
          data-pendo-mask
        />
        <input
          type="number"
          placeholder="Max value"
          value={maxValue}
          onChange={(e) => handleMaxChange(e.target.value)}
          disabled={disabled}
          className={cn(
            'pl-2 pr-2 py-0.5 border-l border-form-border',
            'bg-white text-left',
            'w-full min-w-0',
            !maxValue ? 'text-xs text-neutral-400 py-1' : 'text-sm',
            'appearance-none',
            '[&::-webkit-inner-spin-button]:appearance-none',
            '[&::-webkit-outer-spin-button]:appearance-none',
            'focus-visible:outline-shade-aqua-300'
          )}
          data-pendo-mask
        />
      </div>
      <InputDescription
        description={field.description}
        onHelpVisible={(visible) => {
          setIsHelpVisible(visible);
          onHelpVisible(visible);
        }}
      />
    </div>
  );
};

export default NumberRangeInput;
